import { RuleConditionProperty } from "@features/lead-routing/leadRouting.types"

// The current admissions Typeform ("new signup form", Aug 2024) asks language
// level as four buckets. These are the answer strings shown verbatim, so a Head
// of School reads the same wording the lead saw, not CEFR shorthand. `value` is
// the Campus mapLanguageLevelToScore output the allocator receives; the old
// granular A1–C2 form (which produced the odd scores 1/3/5) is retired, so those
// values no longer occur.
export const LANGUAGE_LEVELS: { value: number; label: string }[] = [
  { value: 0, label: "Beginner (A1-A2)" },
  { value: 2, label: "Intermediate (B1-B2)" },
  { value: 4, label: "Advanced (C1-C2)" },
  { value: 6, label: "Mother tongue" },
]

const LANGUAGE_LABEL: Record<number, string> = Object.fromEntries(
  LANGUAGE_LEVELS.map((level) => [level.value, level.label]),
)

export const languageLevelLabel = (value: number): string =>
  LANGUAGE_LABEL[value] ?? `Level ${value}`

// Field-of-study code → readable name. Source of truth: Campus'
// mapFieldOfInterest groups the Typeform answer into one of these four codes
// (OC = "Tech Orientation Course").
export const PROGRAM_LABEL: Record<string, string> = {
  SE: "Software",
  DA: "Data",
  Cyber: "Cybersecurity",
  OC: "Orientation",
}

export const RESIDENCY_LABEL: Record<string, string> = {
  citizen: "Citizen",
  immigration: "Non-citizen",
}

// QTF tiers a lead can carry. Source of truth: Campus' BGMqlScoreCalculator
// returns 1–6 (1 = best-qualified … 5 = not Agentur-registered, 6 =
// disqualified/ineligible; lower = higher priority). Consultants accept 1–4, so
// 5–6 leads generally fall through to Everyone else.
export const QTF_TIERS = [1, 2, 3, 4, 5, 6]

const LEVEL_PROPERTIES = new Set<RuleConditionProperty>([
  "englishLevel",
  "germanLevel",
])

// Renders a condition value for the rule-card chips: the Typeform answer for a
// language level, a readable name for program / residency; passes others through.
export const AFA_LABEL: Record<string, string> = {
  "agentur-fuer-arbeit": "Agentur für Arbeit",
  jobcenter: "Jobcenter",
  "not-yet": "Not registered yet",
}

export function formatConditionValue(
  property: RuleConditionProperty,
  value: string | number,
): string {
  if (LEVEL_PROPERTIES.has(property)) {
    return languageLevelLabel(Number(value))
  }
  if (property === "program") {
    return PROGRAM_LABEL[String(value)] ?? String(value)
  }
  if (property === "residency") {
    return RESIDENCY_LABEL[String(value)] ?? String(value)
  }
  if (property === "afaRegistration") {
    return AFA_LABEL[String(value)] ?? String(value)
  }
  return String(value)
}

// A dynamic property key turned into a readable label, e.g. "employmentStatus"
// or "employment_status" -> "Employment status". Shared by the rule editor's
// property dropdown and the rule card's condition text.
export function humanizePropertyKey(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
